import React from 'react';
import { cn } from '../../lib/utils';

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** 'md' is the standard row toggle, 'sm' the denser one used for nested lists. */
  size?: 'sm' | 'md';
  disabled?: boolean;
  /** Required when no adjacent visible label is associated with the control. */
  ariaLabel?: string;
}

/**
 * Switch-style checkbox. Extracted from the staff cards so the same track/knob
 * geometry is shared; the pattern is still inlined in a few older screens.
 */
const Toggle: React.FC<ToggleProps> = ({ checked, onChange, size = 'md', disabled = false, ariaLabel }) => (
  <label className={cn('relative flex items-center shrink-0', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only peer"
    />
    <div
      className={cn(
        'bg-zinc-300 dark:bg-zinc-600 rounded-full peer',
        // The real checkbox is sr-only, so without this the control has no visible
        // keyboard focus state at all.
        'peer-focus-visible:ring-4 peer-focus-visible:ring-darkGreenColor/30 dark:peer-focus-visible:ring-darkGreenColor-dark/30',
        'peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white',
        "after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:transition-all",
        'dark:border-zinc-600 peer-checked:bg-darkGreenColor dark:peer-checked:bg-darkGreenColor-dark',
        size === 'md' ? 'w-11 h-6 after:h-5 after:w-5' : 'w-9 h-5 after:h-4 after:w-4'
      )}
    />
  </label>
);

export default Toggle;
